/**
 * Guided handwriting calibration (GAME_DESIGN.md § 6, docs/HANDWRITING.md):
 * the player writes every answer character (0–9, "-", "/", ".") twice, and
 * the samples become personal templates that the recogniser trusts more than
 * the built-in ones.
 *
 * Three stages: intro (tips, saved-sample count, reset) → writing (progress,
 * back / redo / skip / next, save early) → done (a test box that reads what
 * the player writes with the freshly calibrated recogniser).
 *
 * The step sequence is pure state in calibrationPlan.ts; samples reach the
 * recogniser only when the player saves, so "Back" can redo any sample.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import type { HandwritingRecognizer, HwChar, HwStroke } from '../contracts.ts';
import { getLocale, useT } from '../i18n/index.ts';
import {
  type CalibrationState,
  SAMPLES_PER_CHAR,
  applyCalibration,
  collectedSamples,
  currentStep,
  initialCalibration,
  isComplete,
  progressOf,
  recordSample,
  skipStep,
  stepBack,
} from './calibrationPlan.ts';
import { displayChar, displayText, spokenCharKey } from './charDisplay.ts';
import { HwIcon } from './icons.tsx';
import { InkCanvas } from './InkCanvas.tsx';
import { PAD } from './padModel.ts';
import './handwriting.css';

export interface CalibrationPanelProps {
  recognizer: HandwritingRecognizer;
  /**
   * Called when the player finishes or closes calibration. `calibrated` is
   * true when the recogniser holds personal samples afterwards (from this
   * session or an earlier one).
   */
  onDone(calibrated: boolean): void;
}

type Stage = 'intro' | 'write' | 'done';

/** Extra guidance for the characters that aren't digits. */
const HINT_KEYS: Partial<Record<HwChar, string>> = {
  '-': 'hw.hintMinus',
  '/': 'hw.hintSlash',
  '.': 'hw.hintPoint',
};

export function CalibrationPanel({ recognizer, onDone }: CalibrationPanelProps) {
  const t = useT();
  const locale = getLocale();
  const headingId = useId();
  const headingRef = useRef<HTMLHeadingElement>(null);

  const [stage, setStage] = useState<Stage>('intro');
  const [plan, setPlan] = useState<CalibrationState>(initialCalibration);
  const [ink, setInk] = useState<HwStroke[]>([]);
  const [savedCount, setSavedCount] = useState(() => recognizer.userSampleCount());
  const [resetDone, setResetDone] = useState(false);

  const finish = () => onDone(recognizer.userSampleCount() > 0);

  // Move focus to the new heading when the stage changes, so screen readers announce it.
  const shownStage = useRef(stage);
  useEffect(() => {
    if (shownStage.current === stage) return;
    shownStage.current = stage;
    headingRef.current?.focus();
  }, [stage]);

  const start = () => {
    setPlan(initialCalibration());
    setInk([]);
    setResetDone(false);
    setStage('write');
  };

  const forget = () => {
    recognizer.clearUserSamples();
    setSavedCount(recognizer.userSampleCount());
    setResetDone(true);
  };

  /** Save what was collected and show the test box (or close when nothing was written). */
  const save = (final: CalibrationState) => {
    const added = applyCalibration(recognizer, collectedSamples(final));
    setSavedCount(recognizer.userSampleCount());
    setPlan(final);
    setInk([]);
    if (added === 0) finish();
    else setStage('done');
  };

  const advance = (next: CalibrationState) => {
    setInk([]);
    if (isComplete(next)) save(next);
    else setPlan(next);
  };

  if (stage === 'intro') {
    return (
      <section className="hw-cal" aria-labelledby={headingId}>
        <h2 id={headingId} ref={headingRef} tabIndex={-1} className="hw-cal__title">
          {t('hw.calTitle')}
        </h2>
        <p className="hw-cal__text">{t('hw.calIntro')}</p>
        <Tips t={t} />
        {savedCount > 0 && (
          <div className="hw-cal__saved">
            <p className="hw-cal__text">{t('hw.calSavedCount', { count: savedCount })}</p>
            <button type="button" className="hw-btn hw-btn--quiet" onClick={forget}>
              <HwIcon name="clear" />
              <span className="hw-btn__text">{t('hw.calReset')}</span>
            </button>
          </div>
        )}
        <p className="hw-cal__status" role="status">
          {resetDone ? t('hw.calResetDone') : ''}
        </p>
        <div className="hw-cal__buttons">
          <button type="button" className="hw-btn hw-btn--quiet" onClick={finish}>
            <span className="hw-btn__text">{t('hw.calNotNow')}</span>
          </button>
          <button type="button" className="hw-btn hw-btn--go" onClick={start}>
            <HwIcon name="next" />
            <span className="hw-btn__text">{t('hw.calStart')}</span>
          </button>
        </div>
      </section>
    );
  }

  if (stage === 'done') {
    return (
      <section className="hw-cal" aria-labelledby={headingId}>
        <h2 id={headingId} ref={headingRef} tabIndex={-1} className="hw-cal__title">
          {t('hw.calDoneTitle')}
        </h2>
        <p className="hw-cal__text">{t('hw.calDoneBody')}</p>
        <TryBox recognizer={recognizer} t={t} />
        <div className="hw-cal__buttons">
          <button type="button" className="hw-btn hw-btn--go" onClick={finish}>
            <HwIcon name="check" />
            <span className="hw-btn__text">{t('hw.calFinish')}</span>
          </button>
        </div>
      </section>
    );
  }

  const step = currentStep(plan);
  if (!step) return null; // unreachable: completing the last step leaves the 'write' stage
  const { done, total } = progressOf(plan);
  const shown = displayChar(step.char, locale);
  const spokenKey = spokenCharKey(step.char);
  const spoken = spokenKey ? t(spokenKey) : step.char;
  const hintKey = HINT_KEYS[step.char];
  const hasInk = ink.length > 0;

  return (
    <section className="hw-cal" aria-labelledby={headingId}>
      <h2 id={headingId} ref={headingRef} tabIndex={-1} className="hw-cal__title">
        {t('hw.calTitle')}
      </h2>

      <div
        className="hw-cal__progress"
        role="progressbar"
        aria-label={t('hw.calProgressLabel')}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        aria-valuetext={t('hw.calProgress', { done, total })}
      >
        <div className="hw-cal__progress-fill" style={{ width: `${(100 * done) / total}%` }} />
      </div>
      <p className="hw-cal__progress-text" aria-hidden="true">
        {t('hw.calProgress', { done, total })}
      </p>

      <div className="hw-cal__step">
        <div className="hw-cal__target" aria-hidden="true">
          {shown}
        </div>
        <div className="hw-cal__prompt" aria-live="polite">
          <p className="hw-cal__prompt-main">
            {t('hw.calPrompt', { char: spoken })}{' '}
            <span className="hw-cal__repeat">{t('hw.calRepeat', { n: step.repeat + 1, total: SAMPLES_PER_CHAR })}</span>
          </p>
          {hintKey && <p className="hw-cal__hint">{t(hintKey)}</p>}
        </div>
      </div>

      <InkCanvas
        className="hw-cal__ink"
        strokes={ink}
        label={t('hw.calPadLabel', { char: spoken })}
        guide
        onStrokeEnd={(stroke) => setInk((cur) => [...cur, stroke])}
      />

      <div className="hw-cal__buttons">
        <button
          type="button"
          className="hw-btn hw-btn--quiet"
          aria-label={t('hw.calBackLabel')}
          disabled={plan.index === 0}
          onClick={() => {
            setInk([]);
            setPlan(stepBack(plan));
          }}
        >
          <HwIcon name="back" />
          <span className="hw-btn__text">{t('hw.calBack')}</span>
        </button>
        <button
          type="button"
          className="hw-btn"
          aria-label={t('hw.calRedoLabel')}
          disabled={!hasInk}
          onClick={() => setInk([])}
        >
          <HwIcon name="redo" />
          <span className="hw-btn__text">{t('hw.calRedo')}</span>
        </button>
        <button type="button" className="hw-btn" onClick={() => advance(skipStep(plan))}>
          <HwIcon name="skip" />
          <span className="hw-btn__text">{t('hw.calSkip')}</span>
        </button>
        <button type="button" className="hw-btn hw-btn--go" disabled={!hasInk} onClick={() => advance(recordSample(plan, ink))}>
          <HwIcon name="next" />
          <span className="hw-btn__text">{t('hw.calNext')}</span>
        </button>
      </div>

      <div className="hw-cal__footer">
        <button type="button" className="hw-btn hw-btn--quiet" onClick={() => save(hasInk ? recordSample(plan, ink) : plan)}>
          <HwIcon name="check" />
          <span className="hw-btn__text">{t('hw.calSaveFinish')}</span>
        </button>
      </div>
    </section>
  );
}

function Tips({ t }: { t: ReturnType<typeof useT> }) {
  return (
    <div className="hw-cal__tips">
      <h3 className="hw-cal__subtitle">{t('hw.calTipsTitle')}</h3>
      <ul className="hw-cal__tip-list">
        <li>{t('hw.tipBig')}</li>
        <li>{t('hw.tipOrder')}</li>
        <li>{t('hw.tipFraction')}</li>
        <li>{t('hw.tipDot')}</li>
      </ul>
    </div>
  );
}

/** A small test pad that reads back what the player writes, using the calibrated recogniser. */
function TryBox({ recognizer, t }: { recognizer: HandwritingRecognizer; t: ReturnType<typeof useT> }) {
  const [strokes, setStrokes] = useState<HwStroke[]>([]);
  const [text, setText] = useState('');
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  useEffect(() => () => clearTimeout(timer.current), []);

  const read = useCallback(
    (ink: HwStroke[]) => {
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setText(recognizer.recognize(ink).text), PAD.RECOGNIZE_DELAY_MS);
    },
    [recognizer],
  );

  return (
    <div className="hw-cal__try">
      <InkCanvas
        className="hw-cal__ink hw-cal__ink--wide"
        strokes={strokes}
        label={t('hw.calTryLabel')}
        guide
        onStrokeStart={() => clearTimeout(timer.current)}
        onStrokeEnd={(stroke) => {
          const next = [...strokes, stroke];
          setStrokes(next);
          read(next);
        }}
      />
      <div className="hw-cal__try-row">
        <p className="hw-cal__try-result" role="status">
          {text ? t('hw.calTryResult', { text: displayText(text, getLocale()) }) : t('hw.calTryEmpty')}
        </p>
        <button
          type="button"
          className="hw-btn"
          disabled={!strokes.length}
          onClick={() => {
            clearTimeout(timer.current);
            setStrokes([]);
            setText('');
          }}
        >
          <HwIcon name="clear" />
          <span className="hw-btn__text">{t('hw.calTryClear')}</span>
        </button>
      </div>
    </div>
  );
}
